// API token page helpers: scopes, expiry choices, state and the snippets shown
// after a token is created.
import type { ApiScope, ApiToken, PointAccess } from '../api/types.js';
import { parseTagFilter } from './selection.js';

export const SCOPE_INFO: readonly { scope: ApiScope; label: string; help: string }[] = [
  {
    scope: 'read',
    label: 'read',
    help: 'Viewer: read sites, edges, points, live values, history and alarms. Enough for the MCP server and reports.',
  },
  {
    scope: 'control:write',
    label: 'control:write',
    help: 'Operator: read plus acknowledge alarms, edit rules, holidays, tags and limits (commands to BACnet points later).',
  },
  {
    scope: 'admin',
    label: 'admin',
    help: 'Tenant admin: operator plus users and tokens of the tenant. A token of platform staff without sites is a platform token (everything).',
  },
];

/** Expiry choices in days; null = never. */
export const EXPIRY_OPTIONS: readonly { days: number | null; label: string }[] = [
  { days: 30, label: '30 days' },
  { days: 90, label: '90 days' },
  { days: 365, label: '1 year' },
  { days: null, label: 'Never' },
];
export const DEFAULT_EXPIRY_DAYS = 90;

export type TokenState = 'active' | 'expired' | 'revoked';

export function tokenState(t: ApiToken, now: number): TokenState {
  if (t.revoked_at !== null) return 'revoked';
  if (t.expires_at !== null && t.expires_at <= now) return 'expired';
  return 'active';
}

/** The sites a token may see, by name ("All sites" when unrestricted). */
export function tokenSites(
  t: Pick<ApiToken, 'access'>,
  sites: readonly { id: string; name: string }[] | undefined,
): string {
  const ids = t.access?.site_ids;
  if (!ids?.length) return 'All sites';
  const names = new Map((sites ?? []).map((s) => [s.id, s.name]));
  return ids.map((id) => names.get(id) ?? id).join(', ');
}

/** How a new token is limited to points ('none' = every point of its sites). */
export type PointMode = 'none' | 'list' | 'enabled' | 'tags';

export const POINT_MODES: readonly { mode: PointMode; label: string; help: string }[] = [
  { mode: 'none', label: 'All points', help: 'Every point of the allowed sites.' },
  { mode: 'list', label: 'Specific points', help: 'Only the points picked below.' },
  {
    mode: 'enabled',
    label: 'All enabled except',
    help: 'Every point that is read (enabled), except the ones picked below. Points enabled later are included automatically.',
  },
  {
    mode: 'tags',
    label: 'By tag',
    help: 'Points carrying any of these tags (key=value, or a key for any value). Tags added to points later apply automatically.',
  },
];

/** Same limits as central-api (`POINT_ACCESS_LIMITS`). */
export const POINT_ACCESS_LIMITS = { ids: 5000, tags: 20 } as const;

/** A tag filter in its canonical form (`key=value` or `key`); null when invalid. */
export function normalizeTagFilter(text: string): string | null {
  const f = parseTagFilter(text);
  return f ? (f.value === null ? f.key : `${f.key}=${f.value}`) : null;
}

/**
 * The `points` body of a new token, or why it cannot be sent. `none` →
 * `{ok: true, points: undefined}`.
 */
export function pointAccessBody(
  mode: PointMode,
  ids: readonly string[],
  tags: readonly string[],
  scopes: ReadonlySet<ApiScope>,
): { ok: true; points: PointAccess | undefined } | { ok: false; problem: string } {
  if (mode === 'none') return { ok: true, points: undefined };
  if ([...scopes].some((s) => s !== 'read')) {
    return { ok: false, problem: 'A token restricted to points can only have the read scope.' };
  }
  const unique = [...new Set(ids)];
  if (unique.length > POINT_ACCESS_LIMITS.ids) {
    return { ok: false, problem: `At most ${String(POINT_ACCESS_LIMITS.ids)} points.` };
  }
  if (mode === 'list') {
    if (unique.length === 0) return { ok: false, problem: 'Pick at least one point.' };
    return { ok: true, points: { mode: 'list', ids: unique } };
  }
  if (mode === 'enabled') return { ok: true, points: { mode: 'enabled', except: unique } };
  const canonical = tags.map((t) => normalizeTagFilter(t));
  if (canonical.some((t) => t === null)) return { ok: false, problem: 'Fix the invalid tags.' };
  const valid = [...new Set(canonical as string[])];
  if (valid.length === 0) return { ok: false, problem: 'Add at least one tag.' };
  if (valid.length > POINT_ACCESS_LIMITS.tags) {
    return { ok: false, problem: `At most ${String(POINT_ACCESS_LIMITS.tags)} tags.` };
  }
  return { ok: true, points: { mode: 'tags', tags: valid } };
}

/** The point restriction of a listed token in words ("All points" when none). */
export function tokenPoints(t: Pick<ApiToken, 'access'>): string {
  const p = t.access?.points;
  if (!p) return 'All points';
  const n = (count: number | undefined, one: string, many: string) =>
    `${String(count ?? 0)} ${(count ?? 0) === 1 ? one : many}`;
  switch (p.mode) {
    case 'list':
      return n(p.count, 'point', 'points');
    case 'enabled':
      return p.except_count
        ? `All enabled except ${n(p.except_count, 'point', 'points')}`
        : 'All enabled';
    case 'tags':
      return `Tagged ${(p.tags ?? []).join(' or ')}`;
    default:
      return 'Restricted';
  }
}

/** Port of the MCP server next to the UI (`deploy/dev/compose.yaml`). */
export const MCP_PORT = 8095;

/**
 * The MCP endpoint: `VITE_MCP_URL` when the build sets it, else plain HTTP
 * on the UI's host at port 8095 (the server has no TLS of its own yet).
 */
export function mcpUrl(location: Pick<Location, 'hostname'>, configured?: string): string {
  if (configured?.trim()) return configured.trim().replace(/\/+$/, '');
  return `http://${location.hostname}:${MCP_PORT}/mcp`;
}

/** Ready-to-paste commands for a new token. */
export function tokenSnippets(token: string, urls: { api: string; mcp: string }) {
  const auth = `"Authorization: Bearer ${token}"`;
  return {
    claudeCode: `claude mcp add --transport http kalvio ${urls.mcp} --header ${auth}`,
    curl: `curl -H ${auth} '${urls.api.replace(/\/+$/, '')}/api/v1/points?limit=10'`,
  };
}
